import React, {useState, useEffect, Fragment} from 'react';
import {useFieldArray, Controller, get, useWatch} from 'react-hook-form';
import SubHeader from 'app/gakki/components/SubHeader';
import Select from 'components/Select';
import {transferDateObj, formatYYYYMMDD} from 'app/gakki/utils/time';
import {isInvalidID} from 'app/gakki/utils/validate';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlusCircle} from '@fortawesome/free-solid-svg-icons';
import {IconButton} from '../../../../../components/Button';
import {
  FormItem,
  Input,
  DatePicker,
  ErrorMessage,
  Switch,
} from 'components/form-items';
import {DateTime} from 'luxon';
import Block from './block';
import DependentsShow from './dependents-show';
import DependentsBlock from './dependents-block';
import DependentsIcon from './dependents-icon';
import {DeleteDialog} from '../../../components/Dialog';
import {isNationalIdentificationNumberValid} from 'taiwan-id-validator';

const nationality = [
  {
    label: '本國人',
    value: 0,
  },
  {
    label: '外籍',
    value: 1,
  },
];

const deductionType = [
  {
    label: '補助費率',
    value: 0,
  },
  {
    label: '最高補助金額',
    value: 1,
  },
];

const yesorno = [
  {
    label: '是',
    value: true,
  },
  {
    label: '否',
    value: false,
  },
];

const DependentsForm = (props: any) => {
  const {
    formProps,
    defaultValues,
    defaultFields,
    options,
    onBoardingDate,
    dependentsFieldName,
    isUnable,
    isError,
    renderErrorMessage,
    index,
    updateLocale,
    presetEffectiveDate,
    shouldPresetEffectiveDate,
  } = props;
  const {control, formState, trigger} = formProps;
  const dependentsFieldValue = useWatch({
    control,
    name: dependentsFieldName,
    defaultValue: defaultFields.dependentsHealthInsuranceItems,
  });
  const isDeduction = useWatch({
    control,
    name: `${dependentsFieldName}.${index}.isDeductionType`,
    defaultValue: defaultValues.isDeductionType,
  });
  const deductionRateType = useWatch({
    control,
    name: `${dependentsFieldName}.${index}.deductionRateType`,
    defaultValue: defaultValues.deductionRateType,
  });
  const addInsuranceDate = useWatch({
    control,
    name: `${dependentsFieldName}.${index}.addInsuranceDate`,
    defaultValue: defaultValues.addInsuranceDate,
  });
  const nationalityValue = useWatch({
    control,
    name: `${dependentsFieldName}.${index}.nationality`,
    defaultValue: defaultValues.nationality,
  });

  useEffect(() => {
    if (formState.isSubmitted) {
      trigger(`${dependentsFieldName}.${index}.identityNo`);
    }
  }, [nationalityValue, formState]);
  return (
    <DependentsBlock>
      <FormItem
        label="加保日"
        itemName="nhi_dependents_addInsuranceDate"
        required
        width="160px"
        data-test-id={'add-insurance-date'}
      >
        <Controller
          render={({onChange, value}) => (
            <Fragment>
              <DatePicker
                dateFormat="yyyy/MM/dd"
                minDate={transferDateObj(onBoardingDate)}
                selected={transferDateObj(value)}
                disabled={formState.isSubmitting || isUnable}
                onChange={(date: Date) => {
                  const dateString = formatYYYYMMDD(date);
                  onChange(dateString);
                  updateLocale();
                }}
                onBlur={() => {
                  if (value && shouldPresetEffectiveDate === 'true') {
                    presetEffectiveDate(value)
                  }
                }}
                onCalendarClose={() => {
                  if (value && shouldPresetEffectiveDate === 'true') {
                    presetEffectiveDate(value)
                  }
                }}
                error={isError(
                  `${dependentsFieldName}.${index}.addInsuranceDate`,
                )}
              />
              {renderErrorMessage(`${dependentsFieldName}.${index}.addInsuranceDate`)}
            </Fragment>
          )}
          name={`${dependentsFieldName}.${index}.addInsuranceDate`}
          control={control}
          rules={{
            required: !isUnable && '加保日為必填',
            validate: {
              isAfterOnBoardingDate: v => {
                let onBoardingDateObj = transferDateObj(onBoardingDate);
                let addInsuranceDateObj = transferDateObj(v);
                if (!isUnable && onBoardingDateObj && addInsuranceDateObj) {
                  return (
                    DateTime.fromJSDate(onBoardingDateObj) <=
                      DateTime.fromJSDate(addInsuranceDateObj) ||
                    `不可早於員工到職日${onBoardingDate}`
                  );
                }
                return true;
              },
            },
          }}
          defaultValue={defaultValues.addInsuranceDate}
        />
      </FormItem>
      <FormItem
        label="眷屬姓名"
        itemName="nhi_dependents_name"
        required
        width="100px"
        data-test-id={'name'}
      >
        <Controller
          render={({onChange, value}) => (
            <Input
              value={value}
              disabled={formState.isSubmitting || isUnable}
              onChange={(e: any) => {
                onChange(e);
                updateLocale();
              }}
              maxLength={20}
              error={isError(`${dependentsFieldName}.${index}.name`)}
            />
          )}
          name={`${dependentsFieldName}.${index}.name`}
          control={control}
          rules={{required: !isUnable && '眷屬姓名為必填'}}
          defaultValue={defaultValues.name}
        />
        {renderErrorMessage(`${dependentsFieldName}.${index}.name`)}
      </FormItem>
      <FormItem
        label="稱謂"
        itemName="nhi_dependents_nHIDependentsAddTitleCode"
        required
        width="135px"
        data-test-id={'nhi-dependents-add-title-code'}
      >
        <Controller
          render={({onChange, value}) => (
            <Select
              value={
                options.nHIDependentsAddTitle.find(
                  (o: any) => o.value === value,
                ) || null
              }
              options={options.nHIDependentsAddTitle}
              onChange={(o: {value: string}) => {
                onChange(o.value);
                updateLocale();
              }}
              isDisabled={formState.isSubmitting || isUnable}
              getOptionLabel={(o: any) => o.item}
              error={isError(
                `${dependentsFieldName}.${index}.nHIDependentsAddTitleCode`,
              )}
            />
          )}
          name={`${dependentsFieldName}.${index}.nHIDependentsAddTitleCode`}
          control={control}
          rules={{required: !isUnable && '稱謂為必填'}}
          defaultValue={defaultValues.nHIDependentsAddTitleCode}
        />
      </FormItem>
      <FormItem
        label="籍別"
        itemName="nhi_dependents_nationality"
        required
        width="100px"
        data-test-id={'nationality'}
      >
        <Controller
          render={({onChange, value}) => (
            <Select
              value={nationality.find(o => o.value === value)}
              options={nationality}
              onChange={async (o: {value: string}) => {
                onChange(o.value);
                updateLocale();
              }}
              isDisabled={formState.isSubmitting || isUnable}
            />
          )}
          name={`${dependentsFieldName}.${index}.nationality`}
          control={control}
          defaultValue={defaultValues.nationality}
        />
      </FormItem>
      <FormItem
        label="證件號碼"
        itemName="nhi_dependents_identityNo"
        required
        width="135px"
        data-test-id={'identity-no'}
      >
        <Controller
          render={({onChange, value}) => (
            <Input
              value={value}
              disabled={formState.isSubmitting || isUnable}
              onChange={(e: any) => {
                onChange(e);
                updateLocale();
              }}
              maxLength={20}
              error={isError(`${dependentsFieldName}.${index}.identityNo`)}
            />
          )}
          name={`${dependentsFieldName}.${index}.identityNo`}
          control={control}
          rules={{
            required: !isUnable && '證件號碼為必填',
            validate: {
              isValid: v => {
                return !isUnable &&
                  ((nationalityValue === 0 &&
                    !isNationalIdentificationNumberValid(v)) ||
                    (nationalityValue === 1 && isInvalidID(v, true)))
                  ? '證件號碼錯誤'
                  : true;
              },
              isRepeat: v => {
                return !isUnable &&
                  dependentsFieldValue.some(
                    (f: any, compareIdx: number) =>
                      f.identityNo === v && compareIdx !== index,
                  )
                  ? '證件號碼不可重覆'
                  : true;
              },
            },
          }}
          defaultValue={defaultValues.identityNo}
        />
        {renderErrorMessage(`${dependentsFieldName}.${index}.identityNo`)}
      </FormItem>
      <FormItem
        label="生日"
        itemName="nhi_dependents_birthday"
        required
        width="170px"
        data-test-id={'birthday'}
      >
        <Controller
          render={({onChange, value}) => (
            <DatePicker
              dateFormat="yyyy/MM/dd"
              selected={transferDateObj(value)}
              disabled={formState.isSubmitting || isUnable}
              onChange={(date: Date) => {
                onChange(formatYYYYMMDD(date));
                updateLocale();
              }}
              error={isError(`${dependentsFieldName}.${index}.birthday`)}
            />
          )}
          name={`${dependentsFieldName}.${index}.birthday`}
          control={control}
          rules={{
            required: !isUnable && '生日為必填',
            validate: {
              isAfterOnBoardingDate: v => {
                let addInsuranceDateObj = transferDateObj(addInsuranceDate);
                let birthdayObj = transferDateObj(v);
                if (!isUnable && addInsuranceDateObj && birthdayObj) {
                  return (
                    DateTime.fromJSDate(birthdayObj) <=
                      DateTime.fromJSDate(addInsuranceDateObj) ||
                    `不可晚於加保日${formatYYYYMMDD(addInsuranceDate)}`
                  );
                }
                return true;
              },
            },
          }}
          defaultValue={defaultValues.birthday}
        />
        {renderErrorMessage(`${dependentsFieldName}.${index}.birthday`)}
      </FormItem>
      <FormItem
        label="收費"
        itemName="nhi_dependents_isChargeType"
        required
        width="80px"
        data-test-id={'is-charge-type'}
      >
        <Controller
          render={({onChange, value}) => (
            <Select
              value={yesorno.find(o => o.value === value)}
              options={yesorno}
              onChange={(o: {value: string}) => {
                onChange(o.value);
                updateLocale();
              }}
              isDisabled={formState.isSubmitting || isUnable}
            />
          )}
          name={`${dependentsFieldName}.${index}.isChargeType`}
          control={control}
          defaultValue={defaultValues.isChargeType}
        />
      </FormItem>
      <FormItem
        label="減免身分"
        itemName="nhi_dependents_isDeductionType"
        required
        width="80px"
        data-test-id={'is-deduction-type'}
      >
        <Controller
          render={({onChange, value}) => (
            <Select
              value={yesorno.find(o => o.value === value)}
              options={yesorno}
              onChange={(o: {value: string}) => {
                onChange(o.value);
                updateLocale();
              }}
              isDisabled={formState.isSubmitting || isUnable}
            />
          )}
          name={`${dependentsFieldName}.${index}.isDeductionType`}
          control={control}
          defaultValue={defaultValues.isDeductionType}
        />
      </FormItem>
      <FormItem
        label="減免類型"
        itemName="nhi_dependents_deductionRateType"
        required
        width="135px"
        hidden={!isDeduction}
        data-test-id={'deduction-rate-type'}
      >
        <Controller
          render={({onChange, value}) => (
            <Select
              value={deductionType.find(o => o.value === value)}
              options={deductionType}
              onChange={(o: {value: string}) => {
                onChange(o.value);
                updateLocale();
              }}
              isDisabled={formState.isSubmitting || isUnable}
            />
          )}
          name={`${dependentsFieldName}.${index}.deductionRateType`}
          control={control}
          defaultValue={defaultValues.deductionRateType}
        />
      </FormItem>
      <FormItem
        label="補助費率"
        itemName="nhi_dependents_allowanceRate"
        required
        width="100px"
        hidden={!isDeduction || deductionRateType !== 0}
        data-test-id={'allowance-rate'}
      >
        <Controller
          render={({onChange, value}) => (
            <Input
              value={value}
              disabled={formState.isSubmitting || isUnable}
              onChange={e => {
                const {value} = e.target;
                if (!value || /^(\d+(\.\d{0,2})?|\.?\d{1,2})$/.test(value)) {
                  onChange(value);
                  updateLocale();
                }
              }}
              error={
                deductionRateType === 0 &&
                isDeduction &&
                isError(`${dependentsFieldName}.${index}.allowanceRate`)
              }
            />
          )}
          name={`${dependentsFieldName}.${index}.allowanceRate`}
          control={control}
          rules={{
            required:
              deductionRateType === 0 &&
              isDeduction &&
              !isUnable &&
              '補助費率為必填',
            min: {
              value: 0,
              message: '最小值為0',
            },
            max: {
              value: 100,
              message: '最大值為100',
            },
          }}
          defaultValue={defaultValues.allowanceRate}
        />
        {renderErrorMessage(`${dependentsFieldName}.${index}.allowanceRate`)}
      </FormItem>
      <FormItem
        label="最高補助金額"
        itemName="nhi_dependents_highestAllowanceAmount"
        required
        width="120px"
        hidden={!isDeduction || deductionRateType !== 1}
        data-test-id={'highest-allowance-amount'}
      >
        <Controller
          render={({onChange, value}) => (
            <Input
              value={value}
              disabled={formState.isSubmitting || isUnable}
              onChange={e => {
                const {value} = e.target;
                if (!value || /\d/.test(value)) {
                  onChange(value);
                  updateLocale();
                }
              }}
              maxLength={9}
              error={
                deductionRateType === 1 &&
                isDeduction &&
                isError(
                  `${dependentsFieldName}.${index}.highestAllowanceAmount`,
                )
              }
            />
          )}
          name={`${dependentsFieldName}.${index}.highestAllowanceAmount`}
          control={control}
          rules={{
            required:
              deductionRateType === 1 &&
              isDeduction &&
              !isUnable &&
              '最高補助金額為必填',
          }}
          defaultValue={defaultValues.highestAllowanceAmount}
        />
        {renderErrorMessage(
          `${dependentsFieldName}.${index}.highestAllowanceAmount`,
        )}
      </FormItem>
      <FormItem
        label="減免備註"
        itemName="nhi_dependents_allowanceRemark"
        width="100px"
        hidden={!isDeduction}
        data-test-id={'allowance-remark'}
      >
        <Controller
          render={({onChange, value}) => (
            <Input
              value={value}
              disabled={formState.isSubmitting || isUnable}
              onChange={(e: any) => {
                onChange(e);
                updateLocale();
              }}
              maxLength={250}
            />
          )}
          name={`${dependentsFieldName}.${index}.allowanceRemark`}
          control={control}
          defaultValue={defaultValues.allowanceRemark}
        />
      </FormItem>
    </DependentsBlock>
  );
};
const NhiDependentsData = (props: any) => {
  const {
    formProps,
    defaultValues,
    options,
    updateLocale,
    defaultNHIEnabled,
  } = props;
  const {control, formState, errors, trigger} = formProps;
  const dependentsFieldName = `dependentsHealthInsurance.dependentsHealthInsuranceItems`;
  const dependentsField = useFieldArray({
    control: formProps.control,
    name: dependentsFieldName,
    keyName: 'dependentsHealthInsuranceId',
  });
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
  const isNHIUnable = !useWatch({
    control,
    name: `healthInsurance.enabled`,
    defaultValue: defaultNHIEnabled,
  });
  const isUnable = !useWatch({
    control,
    name: `dependentsHealthInsurance.enabled`,
    defaultValue: defaultValues.enabled,
  });
  const isError = (name: string) =>
    !isUnable && formState.isSubmitted && get(errors, name);
  const renderErrorMessage = (name: string) =>
    isError(name) && <ErrorMessage>{get(errors, name)?.message}</ErrorMessage>;

  useEffect(() => {
    if (!isUnable && formState.isSubmitted) {
      trigger();
    }
  }, [isUnable, formState.isSubmitted]);

  return (
    <div data-test-id={'dependents-health-insurance'}>
      <SubHeader>
        <span style={{paddingRight: '15px'}}>健保眷屬加保</span>
        <Controller
          render={({onChange, value}) => (
            <div data-test-id={'enable-dependents-health-insurance'} className="flex items-center">
              <Switch
                checked={value}
                onChange={(e: any) => {
                  onChange(e);
                  updateLocale();
                }}
                disabled={isNHIUnable}
              />
            </div>
          )}
          name={`dependentsHealthInsurance.enabled`}
          control={control}
          defaultValue={defaultValues.enabled}
        />
      </SubHeader>
      <Block padding="0 20px">
        {dependentsField.fields.map((f: any, index: number) => {
          return (
            <DependentsShow
              key={f.dependentsHealthInsuranceId}
              isShow
              data-test-id={`dependents-${index}`}
            >
              <DependentsForm
                {...props}
                options={options}
                defaultValues={f}
                defaultFields={defaultValues}
                dependentsFieldName={dependentsFieldName}
                isUnable={isUnable}
                isError={isError}
                renderErrorMessage={renderErrorMessage}
                index={index}
              />
              <DependentsIcon>
                {index === 0 && (
                  <IconButton
                    onClick={() => {
                      dependentsField.append({
                        nationality: 0,
                        name: '',
                        identityNo: '',
                        birthday: '',
                        nHIDependentsAddTitleCode: '',
                        isChargeType: true,
                        isDeductionType: false,
                        deductionRateType: 0,
                        allowanceRate: '0',
                        highestAllowanceAmount: '0',
                        allowanceRemark: '',
                        addInsuranceDate: '',
                      });
                      updateLocale();
                    }}
                    disabled={isUnable}
                    data-test-id={'add-btn'}
                  >
                    <FontAwesomeIcon
                      icon={faPlusCircle}
                      style={{fontSize: 28}}
                    />
                  </IconButton>
                )}
                {dependentsField.fields.length > 1 && (
                  <IconButton
                    onClick={e => {
                      e.preventDefault();
                      setDeleteIdx(index);
                    }}
                    disabled={isUnable}
                  >
                    <FontAwesomeIcon
                      icon={['fal', 'trash-alt']}
                      style={{fontSize: 18}}
                    />
                  </IconButton>
                )}
              </DependentsIcon>
            </DependentsShow>
          );
        })}
        <DeleteDialog
          isOpen={deleteIdx !== null}
          onCancel={() => setDeleteIdx(null)}
          onConfirm={() => {
            if (deleteIdx !== null) {
              dependentsField.remove(deleteIdx);
            }
            updateLocale();
            setDeleteIdx(null);
          }}
        />
      </Block>
    </div>
  );
};

export default NhiDependentsData;
